import type {
    QuickFilterConfig,
    QuickFilterState,
} from 'shared/filter_pane/QuickFilterPane';

/**
 * Determines if a quick filter preset is active based on the current filter state.
 *
 * For array filters, checks if ALL of the filter's values are included in the
 * current state (inclusion check, not exact match). This allows multiple quick
 * filters targeting the same field to be active simultaneously.
 *
 * For non-array filters, uses strict equality comparison.
 */
export function isQuickFilterActive<T extends QuickFilterState>(
    filter: QuickFilterConfig<T>,
    currentFilters: T
): boolean {
    const currentValue = currentFilters[filter.field];

    if (filter.isArray) {
        if (!Array.isArray(currentValue)) {
            return false;
        }
        return filter.value.every((v) => currentValue.includes(v));
    }

    return currentValue === filter.value;
}

/**
 * Toggles a quick filter on/off and returns the updated filter state.
 * Additive for arrays: adds values if not present, removes if all present.
 */
export function toggleQuickFilter<T extends QuickFilterState>(
    config: QuickFilterConfig<T>,
    filters: T
): T {
    const isToggled = isQuickFilterActive(config, filters);

    if (config.isArray) {
        const currentValue = filters[config.field];
        const currentArray = Array.isArray(currentValue) ? currentValue : [];

        const nextValue = !isToggled
            ? [...new Set([...currentArray, ...config.value])]
            : currentArray.filter((v) => !config.value.includes(v));

        return {
            ...filters,
            [config.field]: nextValue,
        } as T;
    }

    return {
        ...filters,
        [config.field]: !isToggled ? config.value : undefined,
    } as T;
}
